import { put, select, takeEvery } from 'redux-saga/effects';

import { Rule } from 'entities/campaign';
import { Actions } from './actions';
import { Sagas as GenericSagas } from './genericService';
import { Selectors } from './selectors';
import { RuleAdded, RuleDeleted, RuleEdited, RulesetCloned, Types, ChannelAdded } from './types';
import { Channel, isOffSiteChannel } from 'entities/campaign/rulesets/channels';
import { uuid } from 'utils';

function* addRule(rulesetId: string, rule: Rule) {
  const ruleset = yield select(Selectors.getUpdatedEntity, rulesetId);
  const rules = !!ruleset && !!ruleset.rules ? [...ruleset.rules, rule] : [rule];
  yield put(Actions.updateLocal(rulesetId, { rules }));
}

function* editRule(rulesetId: string, ruleIndex: number, changes: Rule) {
  const ruleset = yield select(Selectors.getUpdatedEntity, rulesetId);
  const rules = !!ruleset && !!ruleset.rules ? [...ruleset.rules] : [];
  if (rules.length <= ruleIndex) {
    return;
  }

  rules[ruleIndex] = {
    ...rules[ruleIndex],
    ...changes,
  };
  yield put(Actions.updateLocal(rulesetId, { rules }));
}

function* deleteRule(rulesetId: string, ruleIndex: number) {
  const ruleset = yield select(Selectors.getUpdatedEntity, rulesetId);
  let rules = !!ruleset && !!ruleset.rules ? [...ruleset.rules] : [];
  if (rules.length <= ruleIndex) {
    return;
  }

  rules = rules.filter((_, i) => i !== ruleIndex);
  yield put(Actions.updateLocal(rulesetId, { rules }));
}

function* addChannel(rulesetId: string, channel: Channel) {
  const ruleset = yield select(Selectors.getUpdatedEntity, rulesetId);
  const channels = [...ruleset.channels, channel];
  yield put(
    Actions.updateLocal(rulesetId, {
      ...ruleset,
      channels,
    }),
  );
}

function* cloneRuleset(rulesetId: string) {
  const ruleset = yield select(Selectors.getUpdatedEntity, rulesetId);
  if (!!ruleset) {
    const clone = JSON.parse(JSON.stringify(ruleset));
    const newId = yield select(Selectors.newRulesetId);
    if (ruleset.channels !== null && ruleset.channels !== undefined) {
      ruleset.channels.forEach(channel => {
        if (isOffSiteChannel(channel)) {
          channel.cycleId = uuid();
        }
      });
    }
    yield put(Actions.createLocalWithTemporaryId(newId, clone));
  }
}

export function* watchForRuleAction() {
  yield takeEvery(Types.RULE_ADDED, (action: RuleAdded) => addRule(action.payload.rulesetId, action.payload.rule));

  yield takeEvery(Types.RULE_EDITED, (action: RuleEdited) =>
    editRule(action.payload.rulesetId, action.payload.ruleIndex, action.payload.changes),
  );

  yield takeEvery(Types.RULE_DELETED, (action: RuleDeleted) =>
    deleteRule(action.payload.rulesetId, action.payload.ruleIndex),
  );

  yield takeEvery(Types.CHANNEL_ADDED, (action: ChannelAdded) =>
    addChannel(action.payload.rulesetId, action.payload.channel),
  );

  yield takeEvery(Types.RULESET_CLONED, (action: RulesetCloned) => cloneRuleset(action.payload.rulesetId));
}

export const Sagas = [...GenericSagas, watchForRuleAction];
